// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import {
  type EncounterService,
  getPeriodDayWithPausePeriod,
  getReferenceId,
  type LocationAcuityCode,
} from '@quromedical/fhir-common';
import type {
  AdmissionGetParams,
  AdmissionGetResponse,
} from '@quromedical/models';
import { omitby } from '@quromedical/utils';
import { Col } from 'components/base';
import { CrudForm } from 'components/form';
import {
  type ColumnType,
  type SortConfig,
  useTableState,
} from 'components/table';
import type { Field } from 'components/types';
import {
  admissionServiceDisplay,
  admissionStatusDisplay,
  getDisplayDate,
  getPeriodDayDisplay,
} from 'core/display';
import { mapDisplaysToOptionsWithStart } from 'core/forms';
import { PaginatedTable } from 'core/table';
import {
  Dot,
  getAcuityColor,
  Skeleton,
  SkeletonProvider,
  WardCount,
} from 'design-system';
import { Section, Sidebar } from 'design-system/layout';
import { router } from 'expo-router';
import type { EncounterStatusHistory } from 'fhir/r4';
import { useUserSession } from 'hooks';
import { useFiniteState } from 'hooks/useFiniteState';
import { type FetcherOptions, getField } from 'hooks/usePaginatedData';
import { useIsSmallerThan } from 'hooks/useResponsive';
import { AdmissionApi, admissionSWRKey } from 'integration/aggregate';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { strings } from 'strings';
import { trpc } from 'trpc';

import { Header } from './Header';

const api = new AdmissionApi();

const getColumns = (): ColumnType<AdmissionGetResponse>[] => [
  {
    accessor: 'patient',
    header: strings.LabelNameAndSurname,
    Cell: ({ value, row }) => {
      const display = value.display;
      const admissionAcuity = row.original.admissionLocation
        .acuity as LocationAcuityCode;
      const color = getAcuityColor(admissionAcuity);
      return (
        <>
          <Dot color={color} /> {display}
        </>
      );
    },
  } as ColumnType<AdmissionGetResponse, 'patient'>,
  {
    accessor: 'status',
    header: strings.AdmissionStatusLabel,
    disableSortBy: false,
    Cell: ({ value = 'unknown' }) => admissionStatusDisplay[value],
  } as ColumnType<AdmissionGetResponse, 'status'>,
  {
    accessor: 'subject',
    header: strings.DateOfBirth,
    // This is a field from a different resource, so it is not sortable by the FHIR API
    disableSortBy: true,
    Cell: ({ value }) => getDisplayDate(value?.general?.birthDate) || '',
  } as ColumnType<AdmissionGetResponse, 'subject'>,
  {
    accessor: 'period',
    header: strings.AdmissionStartLabel,
    disableSortBy: false,
    Cell: ({ value }) => getDisplayDate(value?.start) || '',
  } as ColumnType<AdmissionGetResponse, 'period'>,
  {
    accessor: 'service',
    header: strings.AdmissionServiceLabel,
    //This field is not sortable by FHIR API
    disableSortBy: true,
    Cell: ({ value }) =>
      admissionServiceDisplay[value as EncounterService] || '',
  } as ColumnType<AdmissionGetResponse, 'service'>,
  {
    accessor: 'period',
    id: 'daysOfService',
    header: strings.AdmissionDayOfService,
    // This is a calculated field, so it is not sortable by the FHIR API
    disableSortBy: true,
    Cell: ({ row }) => {
      const { period, statusHistory, status } = row.original;

      const hasPausedHistory = statusHistory?.some(
        (history) => history.status === 'onleave',
      );

      const dayDisplay = hasPausedHistory
        ? getPeriodDayWithPausePeriod(
            statusHistory as EncounterStatusHistory[],
            period,
          )
        : getPeriodDayDisplay(period, 'message');

      return String(dayDisplay ?? '');
    },
  } as ColumnType<AdmissionGetResponse, 'period'>,
];

type Filter = Pick<
  AdmissionGetParams,
  'status' | 'patientName' | 'serviceType'
>;

const all = '__all__';

const allOption = {
  value: all,
  display: strings.SortFilterAll,
};

const inUseStatuses: Pick<
  typeof admissionStatusDisplay,
  'in-progress' | 'onleave' | 'finished' | 'planned'
> = {
  finished: admissionStatusDisplay.finished,
  onleave: admissionStatusDisplay.onleave,
  'in-progress': admissionStatusDisplay['in-progress'],
  planned: admissionStatusDisplay.planned,
};

const filterFields: Field<Filter>[] = [
  {
    subfields: [
      {
        key: 'serviceType',
        type: 'combobox-single',
        label: strings.AdmissionServiceLabel,
        fetcher: mapDisplaysToOptionsWithStart(
          allOption,
          admissionServiceDisplay,
        ),
      },
    ],
  },
  {
    subfields: [
      {
        key: 'status',
        type: 'combobox-single',
        label: strings.AdmissionStatusLabel,
        fetcher: mapDisplaysToOptionsWithStart(allOption, inUseStatuses),
      },
    ],
  },
];

const shouldOmitOption = (value?: string): boolean => {
  if (!value?.length) {
    return true;
  }

  return value === all;
};

const sanitizeFilter = (data: Filter) => omitby(data, shouldOmitOption);

const sortConfig: SortConfig<AdmissionGetResponse> = {
  status: {
    asc: 'status',
    desc: '-status',
  },
  period: {
    asc: 'date',
    desc: '-date',
  },
};

export const AdmissionListScreen: React.FC = () => {
  const userSession = useUserSession();

  const sidebarProps = Sidebar.useResponsiveSidebarProps();
  const state = useFiniteState<FormState>('initial');
  const [filter, setFilter] = useState<Filter>({ status: 'in-progress' });
  const [currentPageData, setCurrentPageData] = useState<
    AdmissionGetResponse[]
  >([]);
  const [tableKey, setTableKey] = useState(Date.now());
  const [selectedPatientIds, setSelectedPatientIds] = useState<Set<string>>(
    new Set(),
  );

  const setPatient = useCallback(
    (patientName?: string) => setFilter({ ...filter, patientName }),
    [filter],
  );

  const tableState = useTableState<AdmissionGetResponse>({ sort: sortConfig });

  const cacheKey = [
    admissionSWRKey,
    filter,
    tableState.sort,
    userSession.ward,
    tableKey,
  ];

  const {
    data: acuityCounts,
    isFetching: isLoading,
    isError,
    refetch,
  } = trpc.admission.getAcuityCount.useQuery();

  const fetcher = useCallback(
    async (options?: FetcherOptions) => {
      const response = await api.getAdmissions({
        ...sanitizeFilter(filter),
        ...options,
        pageToken: options?.nextToken,
        sort: tableState.sort,
        includeAdminAssignments: true,
        location: userSession.ward,
      });

      setCurrentPageData(response.admissions);
      return response;
    },
    [filter, tableState.sort, userSession.ward],
  );

  const onSubmit = useCallback(
    async (result: { wardId: string }) => {
      try {
        state.set('submitting');
        await api.moveAdmissionsToWard({
          patientIds: Array.from(selectedPatientIds),
          wardId: result.wardId,
        });

        setTableKey(Date.now());

        refetch();
        state.set('initial');
      } catch (err) {
        console.error(err);
        state.set('error');
      }
    },
    [state, refetch, selectedPatientIds],
  );

  const onRowPress = useCallback((row: AdmissionGetResponse) => {
    const id = getReferenceId(row.patient);

    if (!id) {
      return;
    }

    router.push({
      pathname: `/patient/${id}/profile`,
    });
  }, []);

  const isSmall = useIsSmallerThan('md');
  const displayCheckboxes = !isSmall;

  const columns = useMemo(() => getColumns(currentPageData), [currentPageData]);

  const patientsAreSelected = selectedPatientIds.size === 0;
  const numberOfAdmissions = selectedPatientIds.size;

  const checkBox = displayCheckboxes
    ? {
        getRowId: (row) => row.patient.id,
        onSelectionChange: setSelectedPatientIds,
      }
    : undefined;

  return (
    <Sidebar.Container>
      <Sidebar.Main {...sidebarProps.main}>
        <Section>
          <Header
            patientsAreSelected={patientsAreSelected}
            isSubmitting={state.is('submitting')}
            numberOfAdmissions={numberOfAdmissions}
            onSearchChange={setPatient}
            screen="admissions"
            onSubmit={onSubmit}
          />
        </Section>
        <View style={{ zIndex: -1 }}>
          <Section hasBottomMargin>
            <Col unsetZIndex>
              <WardCount
                isError={isError}
                acuityCounts={acuityCounts}
                isLoading={isLoading}
              />
            </Col>

            <SkeletonProvider loading={state.is('submitting')}>
              <Skeleton>
                <PaginatedTable
                  initialPage={1}
                  cacheKey={cacheKey}
                  fetcher={fetcher}
                  getDataFromResponse={getField('admissions')}
                  onRowPress={onRowPress}
                  onTableStateChanged={tableState.onChange}
                  columns={columns}
                  checkBox={checkBox}
                />
              </Skeleton>
            </SkeletonProvider>
          </Section>
        </View>
      </Sidebar.Main>
      <Sidebar.Aside {...sidebarProps.aside}>
        <CrudForm<Filter>
          onChangeDebounceTime={0}
          showSubmitButton={false}
          onChange={setFilter}
          initialValues={{
            status: 'in-progress',
          }}
          title={strings.FormTitleSearch}
          fields={filterFields}
        />
      </Sidebar.Aside>
    </Sidebar.Container>
  );
};
